import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Card } from 'antd';
import { API_URL, TOKEN } from '../../../../../variables/constants';
import { InfoSection } from './components/InfoSection';
import { getCanonicalStatus, resolveNumeroSinistre } from './components/utils';
import { StatusHeader } from './components/StatusHeader';
import { ActionButtons } from './components/ActionButtons';
import { EditModal } from './components/EditModal';
import { ResponsabilityModal } from './components/ResponsabilityModal';
import { ChangeTypeModal } from './components/ChangeTypeModal';
import { CloseModal } from './components/CloseModal';


// ============================================================================
// COMPOSANT PRINCIPAL
// ============================================================================

const InformationsGenerales = ({
    sinistre,
    isMobileSinistre,
    montant,
    montantPaye,
    onSinistreUpdate
}) => {
    // États des modales
    const [modals, setModals] = useState({
        edit: false,
        changeType: false,
        close: false,
        responsability: false
    });

    const [audioUrl, setAudioUrl] = useState(null);
    const [loading, setLoading] = useState({
        validate: false,
        refuse: false,
        save: false
    });


    useEffect(() => {
        if (sinistre) {
            sinistre?.noteVocale && fetch(`${API_URL}/api/mobile/sinistre-audio/${sinistre.codeId}`, {
                headers: {
                    Authorization: `Bearer ${TOKEN}`,
                    "Content-Type": "application/json",
                },
            })
                .then(res => res.arrayBuffer())
                .then(audioBytes => {
                    const blob = new Blob([audioBytes], { type: "audio/mpeg" });
                    const url = URL.createObjectURL(blob);
                    setAudioUrl(url);
                })
                .catch(err => {
                    console.error(err);
                });
        }
    }, [sinistre]);

    // Calculs dérivés
    const canonicalStatus = getCanonicalStatus(sinistre?.sortDossier);
    const shouldUseReference = canonicalStatus === 'A' || canonicalStatus === 'RF';
    const numeroSinistre = shouldUseReference
        ? (sinistre?.reference || sinistre?.codeId || '-')
        : resolveNumeroSinistre(sinistre);
    const isClosed = canonicalStatus === 'S' || canonicalStatus === 'T';

    // Gestionnaires de modales
    const openModal = (modalName) => {
        setModals(prev => ({ ...prev, [modalName]: true }));
    };

    const closeModal = (modalName) => {
        setModals(prev => ({ ...prev, [modalName]: false }));
    };

    // Gestionnaires d'actions
    const handleValidate = () => {
        console.log('Validation du dossier');
        setLoading(prev => ({ ...prev, validate: true }));
        // Simulation d'appel API
        setTimeout(() => {
            setLoading(prev => ({ ...prev, validate: false }));
            if (onSinistreUpdate) {
                onSinistreUpdate({ ...sinistre, sortDossier: 'O' });
            }
        }, 1500);
    };

    const handleRefuse = () => {
        console.log('Refus du dossier');
        setLoading(prev => ({ ...prev, refuse: true }));
        setTimeout(() => {
            setLoading(prev => ({ ...prev, refuse: false }));
            if (onSinistreUpdate) {
                onSinistreUpdate({ ...sinistre, sortDossier: 'RF' });
            }
        }, 1500);
    };

    const handleSaveEdit = (formData) => {
        console.log('Sauvegarde des modifications', formData);
        setLoading(prev => ({ ...prev, save: true }));
        setTimeout(() => {
            setLoading(prev => ({ ...prev, save: false }));
            closeModal('edit');
            if (onSinistreUpdate) {
                onSinistreUpdate({ ...sinistre, ...formData });
            }
        }, 1500);
    };

    const handleSaveResponsability = ({ taux, motif }) => {
        console.log('Sauvegarde responsabilité', taux, motif);
        setLoading(prev => ({ ...prev, save: true }));
        setTimeout(() => {
            setLoading(prev => ({ ...prev, save: false }));
            closeModal('responsability');
            if (onSinistreUpdate) {
                onSinistreUpdate({ ...sinistre, tauxResponsabilite: taux });
            }
        }, 1500);
    };



    const handleCloseAction = (action, motif) => {
        console.log('Action de clôture', action, motif);
        setLoading(prev => ({ ...prev, save: true }));
        setTimeout(() => {
            setLoading(prev => ({ ...prev, save: false }));
            closeModal('close');
            if (onSinistreUpdate) {
                const newStatus = action === 'reouverture' ? 'R' : action === 'sanssuite' ? 'S' : 'T';
                onSinistreUpdate({ ...sinistre, sortDossier: newStatus });
            }
        }, 1500);
    };

    // Effets
    useEffect(() => {
        if (sinistre?.noteVocale && sinistre?.codeId) {
            // Simulation du chargement audio
            console.log('Chargement audio pour:', sinistre.codeId);
        }
    }, [sinistre]);

    return (
        <div style={{ padding: 24, background: '#f0f2f5', minHeight: '100vh' }}>
            <Card bordered={false} style={{ maxWidth: 1400, margin: '0 auto', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                <StatusHeader
                    status={canonicalStatus}
                    isMobile={isMobileSinistre}
                    numeroSinistre={numeroSinistre}
                />

                <InfoSection
                    sinistre={sinistre}
                    montant={montant}
                    montantPaye={montantPaye}
                    audioUrl={audioUrl}
                />

                <ActionButtons
                    status={canonicalStatus}
                    onEdit={() => openModal('edit')}
                    onChangeType={() => openModal('changeType')}
                    onValidate={handleValidate}
                    onRefuse={handleRefuse}
                    onClose={() => openModal('close')}
                    onResponsability={() => openModal('responsability')}
                    canEditType={true}
                    validateLoading={loading.validate}
                    refuseLoading={loading.refuse}
                    isClosed={isClosed}
                    tauxResponsabilite={sinistre?.tauxResponsabilite}
                />

                {/* Modales */}
                <EditModal
                    visible={modals.edit}
                    onCancel={() => closeModal('edit')}
                    sinistre={sinistre}
                    onSave={handleSaveEdit}
                    loading={loading.save}
                />

                <ResponsabilityModal
                    sinistre={sinistre}
                    visible={modals.responsability}
                    onCancel={() => closeModal('responsability')}
                    onSave={handleSaveResponsability}
                    loading={loading.save}
                    setLoading={setLoading}
                    initialValue={sinistre?.tauxResponsabilite}
                />

                <ChangeTypeModal
                    visible={modals.changeType}
                    onCancel={() => closeModal('changeType')}
                    loading={loading.save}
                    setLoading={setLoading}
                    sinistre={sinistre}
                    currentType={sinistre?.typeSinistre}
                />

                <CloseModal
                    visible={modals.close}
                    onCancel={() => closeModal('close')}
                    onConfirm={handleCloseAction}
                    loading={loading.save}
                    setLoading={setLoading}
                    isClosed={isClosed}
                    sinistre={sinistre}
                />
            </Card>
        </div>
    );
};

InformationsGenerales.propTypes = {
    sinistre: PropTypes.object,
    isMobileSinistre: PropTypes.bool,
    montant: PropTypes.number,
    onSinistreUpdate: PropTypes.func
};

export default InformationsGenerales;
